import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { useSEO } from "./hooks/useSEO";
import axios from "axios";
import { Menu, X, Phone, Mail, MapPin, Facebook, Instagram, Youtube, Linkedin, ArrowRight, ArrowUpRight, Clock, Users, Award, Briefcase, ChefHat, BookOpen, Wrench, Lightbulb, CheckCircle2, Calendar, Send, GraduationCap, Building2, Utensils, Coffee, Scale, Languages, Monitor, Heart, ExternalLink, LogIn } from "lucide-react";
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_e2613026-af33-4200-832e-53271956be9e/artifacts/6a8xvt6i_Sarvaguna%20Academy.png";

// ============ SVG ILLUSTRATIONS ============
const HeroIllustration = () => (
  <svg viewBox="0 0 500 400" className="w-full h-full">
    {/* Kitchen Background */}
    <rect x="50" y="100" width="400" height="250" rx="20" fill="#FFF5EB" stroke="#F47920" strokeWidth="2"/>
    
    {/* Stove */}
    <rect x="100" y="220" width="120" height="100" rx="8" fill="#E5E7EB"/>
    <circle cx="130" cy="250" r="20" fill="#F47920" opacity="0.3"/>
    <circle cx="130" cy="250" r="12" fill="#F47920"/>
    <circle cx="190" cy="250" r="20" fill="#4CAF50" opacity="0.3"/>
    <circle cx="190" cy="250" r="12" fill="#4CAF50"/>
    
    {/* Pot on stove */}
    <ellipse cx="160" cy="230" rx="45" ry="15" fill="#78716C"/>
    <rect x="115" y="180" width="90" height="50" rx="5" fill="#6B7280"/>
    <ellipse cx="160" cy="180" rx="45" ry="12" fill="#78716C"/>
    
    {/* Steam */}
    <path d="M140 160 Q135 140 145 130" stroke="#D1D5DB" strokeWidth="3" fill="none" strokeLinecap="round"/>
    <path d="M160 155 Q155 130 165 115" stroke="#D1D5DB" strokeWidth="3" fill="none" strokeLinecap="round"/>
    <path d="M180 160 Q175 140 185 125" stroke="#D1D5DB" strokeWidth="3" fill="none" strokeLinecap="round"/>
    
    {/* Chef Character */}
    <circle cx="320" cy="180" r="35" fill="#FBBF24"/>
    <circle cx="310" cy="175" r="4" fill="#1F2937"/>
    <circle cx="330" cy="175" r="4" fill="#1F2937"/>
    <path d="M310 190 Q320 200 330 190" stroke="#1F2937" strokeWidth="3" fill="none" strokeLinecap="round"/>
    
    {/* Chef hat */}
    <ellipse cx="320" cy="145" rx="30" ry="10" fill="white" stroke="#E5E7EB" strokeWidth="2"/>
    <rect x="295" y="120" width="50" height="25" rx="5" fill="white" stroke="#E5E7EB" strokeWidth="2"/>
    <ellipse cx="320" cy="120" rx="25" ry="8" fill="white"/>
    
    {/* Chef body */}
    <rect x="290" y="215" width="60" height="80" rx="10" fill="white" stroke="#E5E7EB" strokeWidth="2"/>
    <line x1="320" y1="235" x2="320" y2="280" stroke="#E5E7EB" strokeWidth="2"/>
    <circle cx="320" cy="245" r="4" fill="#1F2937"/>
    <circle cx="320" cy="265" r="4" fill="#1F2937"/>
    
    {/* Chef arms with utensils */}
    <line x1="290" y1="240" x2="250" y2="200" stroke="#FBBF24" strokeWidth="8" strokeLinecap="round"/>
    <circle cx="250" cy="200" r="10" fill="#FBBF24"/>
    <rect x="235" y="160" width="8" height="40" rx="2" fill="#78716C"/>
    <ellipse cx="239" cy="155" rx="12" ry="8" fill="#F47920"/>
    
    <line x1="350" y1="240" x2="390" y2="210" stroke="#FBBF24" strokeWidth="8" strokeLinecap="round"/>
    <circle cx="390" cy="210" r="10" fill="#FBBF24"/>
    <rect x="385" y="170" width="10" height="40" rx="2" fill="#78716C"/>
    
    {/* Decorative vegetables */}
    <circle cx="420" cy="300" r="15" fill="#4CAF50"/>
    <rect x="417" y="280" width="6" height="12" rx="2" fill="#2E7D32"/>
    
    <ellipse cx="80" cy="280" rx="12" ry="18" fill="#F47920"/>
    <rect x="76" y="255" width="8" height="12" rx="2" fill="#2E7D32"/>
    
    <circle cx="440" cy="180" r="10" fill="#EF4444"/>
    <rect x="438" y="165" width="4" height="10" rx="1" fill="#2E7D32"/>
  </svg>
);

const DualVETIllustration = () => (
  <svg viewBox="0 0 400 300" className="w-full h-full">
    {/* Left side - Kitchen */}
    <rect x="20" y="50" width="160" height="200" rx="15" fill="#FFF5EB" stroke="#F47920" strokeWidth="2"/>
    <text x="100" y="85" textAnchor="middle" fontSize="14" fontWeight="600" fill="#F47920">PRACTICAL</text>
    
    {/* Kitchen elements */}
    <rect x="50" y="110" width="100" height="60" rx="5" fill="#E5E7EB"/>
    <circle cx="75" cy="130" r="12" fill="#F47920"/>
    <circle cx="125" cy="130" r="12" fill="#F47920" opacity="0.5"/>
    <rect x="60" y="95" width="80" height="20" rx="3" fill="#78716C"/>
    
    {/* Apprentice figure */}
    <circle cx="100" cy="200" r="20" fill="#FBBF24"/>
    <circle cx="95" cy="197" r="3" fill="#1F2937"/>
    <circle cx="105" cy="197" r="3" fill="#1F2937"/>
    <path d="M93 207 Q100 212 107 207" stroke="#1F2937" strokeWidth="2" fill="none"/>
    <rect x="85" y="220" width="30" height="25" rx="5" fill="white" stroke="#E5E7EB"/>
    
    {/* Right side - Classroom */}
    <rect x="220" y="50" width="160" height="200" rx="15" fill="#E8F5E9" stroke="#4CAF50" strokeWidth="2"/>
    <text x="300" y="85" textAnchor="middle" fontSize="14" fontWeight="600" fill="#4CAF50">CLASSROOM</text>
    
    {/* Desk */}
    <rect x="240" y="180" width="120" height="10" rx="2" fill="#8B5CF6"/>
    <rect x="250" y="190" width="5" height="40" fill="#8B5CF6"/>
    <rect x="345" y="190" width="5" height="40" fill="#8B5CF6"/>
    
    {/* Book */}
    <rect x="270" y="150" width="60" height="30" rx="3" fill="#F47920"/>
    <line x1="300" y1="150" x2="300" y2="180" stroke="white" strokeWidth="2"/>
    
    {/* Student figure */}
    <circle cx="300" cy="120" r="18" fill="#FBBF24"/>
    <circle cx="295" cy="117" r="2.5" fill="#1F2937"/>
    <circle cx="305" cy="117" r="2.5" fill="#1F2937"/>
    <path d="M294 125 Q300 129 306 125" stroke="#1F2937" strokeWidth="2" fill="none"/>
    
    {/* Arrows connecting */}
    <path d="M185 150 L215 150" stroke="#78716C" strokeWidth="2" strokeDasharray="5,5" markerEnd="url(#arrowhead)"/>
    <defs>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#78716C"/>
      </marker>
    </defs>
    
    {/* Days labels */}
    <text x="100" y="270" textAnchor="middle" fontSize="20" fontWeight="700" fill="#F47920">4 Days</text>
    <text x="300" y="270" textAnchor="middle" fontSize="20" fontWeight="700" fill="#4CAF50">2 Days</text>
  </svg>
);

const CurriculumIllustration = ({ type }) => {
  const icons = {
    professional: (
      <svg viewBox="0 0 80 80" className="w-full h-full">
        <circle cx="40" cy="40" r="35" fill="#FFF5EB"/>
        <path d="M25 50 L40 35 L55 50" stroke="#F47920" strokeWidth="4" fill="none" strokeLinecap="round"/>
        <circle cx="40" cy="30" r="8" fill="#F47920"/>
        <rect x="35" y="50" width="10" height="15" rx="2" fill="#F47920"/>
      </svg>
    ),
    practical: (
      <svg viewBox="0 0 80 80" className="w-full h-full">
        <circle cx="40" cy="40" r="35" fill="#E8F5E9"/>
        <rect x="25" y="30" width="30" height="25" rx="3" fill="#4CAF50"/>
        <ellipse cx="40" cy="30" rx="18" ry="6" fill="#388E3C"/>
        <line x1="40" y1="55" x2="40" y2="65" stroke="#4CAF50" strokeWidth="4"/>
        <line x1="30" y1="65" x2="50" y2="65" stroke="#4CAF50" strokeWidth="4" strokeLinecap="round"/>
      </svg>
    ),
    general: (
      <svg viewBox="0 0 80 80" className="w-full h-full">
        <circle cx="40" cy="40" r="35" fill="#FFF5EB"/>
        <rect x="25" y="25" width="30" height="35" rx="3" fill="#F47920"/>
        <line x1="30" y1="35" x2="50" y2="35" stroke="white" strokeWidth="2"/>
        <line x1="30" y1="42" x2="50" y2="42" stroke="white" strokeWidth="2"/>
        <line x1="30" y1="49" x2="45" y2="49" stroke="white" strokeWidth="2"/>
      </svg>
    ),
    project: (
      <svg viewBox="0 0 80 80" className="w-full h-full">
        <circle cx="40" cy="40" r="35" fill="#E8F5E9"/>
        <circle cx="40" cy="35" r="12" fill="#4CAF50"/>
        <path d="M35 35 L38 40 L48 28" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round"/>
        <rect x="30" y="50" width="20" height="15" rx="2" fill="#4CAF50"/>
      </svg>
    )
  };
  return icons[type] || icons.professional;
};

const FranchiseIllustration = () => (
  <svg viewBox="0 0 300 200" className="w-full h-full">
    {/* Central Kitchen */}
    <rect x="110" y="60" width="80" height="60" rx="8" fill="#F47920"/>
    <text x="150" y="95" textAnchor="middle" fontSize="10" fill="white" fontWeight="600">CENTRAL</text>
    <text x="150" y="108" textAnchor="middle" fontSize="10" fill="white" fontWeight="600">KITCHEN</text>
    
    {/* Satellite 1 */}
    <rect x="20" y="140" width="60" height="45" rx="6" fill="#4CAF50"/>
    <text x="50" y="167" textAnchor="middle" fontSize="8" fill="white">SATELLITE 1</text>
    
    {/* Satellite 2 */}
    <rect x="120" y="140" width="60" height="45" rx="6" fill="#4CAF50"/>
    <text x="150" y="167" textAnchor="middle" fontSize="8" fill="white">SATELLITE 2</text>
    
    {/* Satellite 3 */}
    <rect x="220" y="140" width="60" height="45" rx="6" fill="#4CAF50"/>
    <text x="250" y="167" textAnchor="middle" fontSize="8" fill="white">SATELLITE 3</text>
    
    {/* Connection lines */}
    <line x1="150" y1="120" x2="50" y2="140" stroke="#78716C" strokeWidth="2" strokeDasharray="4,4"/>
    <line x1="150" y1="120" x2="150" y2="140" stroke="#78716C" strokeWidth="2" strokeDasharray="4,4"/>
    <line x1="150" y1="120" x2="250" y2="140" stroke="#78716C" strokeWidth="2" strokeDasharray="4,4"/>
    
    {/* Graduates */}
    <circle cx="50" cy="30" r="15" fill="#FBBF24"/>
    <circle cx="150" cy="20" r="15" fill="#FBBF24"/>
    <circle cx="250" cy="30" r="15" fill="#FBBF24"/>
    
    {/* Arrows from graduates to central */}
    <path d="M50 45 L120 70" stroke="#F47920" strokeWidth="2" markerEnd="url(#arrow2)"/>
    <path d="M150 35 L150 55" stroke="#F47920" strokeWidth="2" markerEnd="url(#arrow2)"/>
    <path d="M250 45 L180 70" stroke="#F47920" strokeWidth="2" markerEnd="url(#arrow2)"/>
    
    <defs>
      <marker id="arrow2" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
        <polygon points="0 0, 8 3, 0 6" fill="#F47920"/>
      </marker>
    </defs>
  </svg>
);

// ============ HEADER ============
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  
  // Apply SEO for current page
  useSEO(location.pathname);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const links = [
    { name: "Home", path: "/" },
    { name: "Program", path: "/program" },
    { name: "About", path: "/about" },
    { name: "Gallery", path: "/gallery" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <>
      <header className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrolled ? "glass shadow-sm" : "bg-transparent"}`} data-testid="header">
        <div className="container-wide">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center" data-testid="logo">
              <img src={LOGO_URL} alt="Sarvaguna Academy" className="h-10 md:h-12" />
            </Link>

            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {links.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`link-underline text-sm font-medium tracking-wide ${location.pathname === link.path ? "text-[#F47920]" : ""}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <a 
                href="https://lms.sarvagunaacademy.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-outline flex items-center gap-2 !py-2 !px-4"
                data-testid="lms-btn"
              >
                <LogIn size={16} />
                Student Login
              </a>
              <Link to="/apply" className="btn btn-primary !py-3 !px-6" data-testid="apply-btn">
                Apply Now
              </Link>
            </div>

            <button className="lg:hidden p-2" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-toggle">
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`fixed inset-0 z-[100] bg-white transition-transform duration-300 ${mobileOpen ? "translate-x-0" : "translate-x-full"}`} data-testid="mobile-menu">
        <div className="p-6">
          <div className="flex items-center justify-between mb-12">
            <img src={LOGO_URL} alt="Sarvaguna Academy" className="h-10" />
            <button onClick={() => setMobileOpen(false)} className="p-2">
              <X size={24} />
            </button>
          </div>
          <nav className="space-y-6">
            {links.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="block text-2xl font-serif"
                onClick={() => setMobileOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <a 
              href="https://lms.sarvagunaacademy.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-outline w-full mt-6 flex items-center justify-center gap-2"
            >
              <LogIn size={18} />
              Student Login (LMS)
            </a>
            <Link to="/apply" className="btn btn-primary w-full mt-4" onClick={() => setMobileOpen(false)}>
              Apply Now
            </Link>
          </nav>
        </div>
      </div>
    </>
  );
};

// ============ FOOTER ============
const Footer = () => {
  const [email, setEmail] = useState("");

  const handleNewsletter = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/newsletter?email=${encodeURIComponent(email)}`);
      toast.success("Subscribed successfully!");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to subscribe");
    }
  };

  return (
    <footer className="bg-[#0D0D0D] text-white" data-testid="footer">
      <div className="container-wide section-padding">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="lg:col-span-5">
            <img src={LOGO_URL} alt="Sarvaguna Academy" className="h-12 mb-6" />
            <p className="text-white/60 mb-8 max-w-md leading-relaxed">
              Building tomorrow's mass catering professionals through dual-vocational education and training. An initiative of Sarvaguna Foods & Kitchen.
            </p>
            <div className="flex gap-4">
              {[
                { icon: Facebook, url: "https://facebook.com/sarvagunaacademy" },
                { icon: Instagram, url: "https://instagram.com/sarvagunaacademy" },
                { icon: Youtube, url: "https://youtube.com" },
                { icon: Linkedin, url: "https://linkedin.com" }
              ].map(({ icon: Icon, url }, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-[#0D0D0D] transition-all">
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-semibold mb-6 text-sm tracking-wider uppercase">Quick Links</h4>
            <nav className="space-y-4">
              {["Program", "About", "Gallery", "Contact", "Apply"].map((item) => (
                <Link key={item} to={`/${item.toLowerCase()}`} className="block text-white/60 hover:text-[#F47920] transition-colors">
                  {item}
                </Link>
              ))}
              <a href="https://lms.sarvagunaacademy.com" target="_blank" rel="noopener noreferrer" className="block text-white/60 hover:text-[#F47920] transition-colors">
                Student LMS
              </a>
            </nav>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-semibold mb-6 text-sm tracking-wider uppercase">Contact</h4>
            <div className="space-y-4 text-white/60">
              <p className="flex items-center gap-3">
                <Phone size={16} className="text-[#F47920]" />
                +977 9802852978
              </p>
              <p className="flex items-center gap-3">
                <Phone size={16} className="text-[#F47920]" />
                +977 9846771411
              </p>
              <p className="flex items-center gap-3">
                <Mail size={16} className="text-[#F47920]" />
                info@sarvagunaacademy.com
              </p>
              <p className="flex items-center gap-3">
                <MapPin size={16} className="text-[#F47920]" />
                Pokhara, Nepal
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-semibold mb-6 text-sm tracking-wider uppercase">Stay Updated</h4>
            <form onSubmit={handleNewsletter} className="space-y-3" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#F47920]"
                required
                data-testid="newsletter-email"
              />
              <button type="submit" className="btn btn-primary w-full" data-testid="newsletter-submit">
                Subscribe
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-white/40 text-sm">
          <p>© {new Date().getFullYear()} Sarvaguna Academy. All rights reserved.</p>
          <p>An initiative of <a href="https://sarvagunakitchen.com" target="_blank" rel="noopener noreferrer" className="text-[#F47920] hover:underline">Sarvaguna Foods & Kitchen</a></p>
        </div>
      </div>
    </footer>
  );
};

// ============ WHATSAPP BUTTON ============
const WhatsAppButton = () => (
  <a href="https://wa.me/9779846279645" target="_blank" rel="noopener noreferrer" className="whatsapp-btn" data-testid="whatsapp-btn">
    <svg viewBox="0 0 24 24" width="26" height="26" fill="white">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  </a>
);

// ============ PARTNER LOGOS ============
const PartnerLogos = () => {
  const partners = [
    { name: "CTEVT", abbr: "CTEVT", color: "#1E40AF" },
    { name: "Sarvaguna Foods & Kitchen", abbr: "SFK", color: "#F47920" },
    { name: "Nepal Food Networks", abbr: "NFN", color: "#4CAF50" },
    { name: "Cuisine Sans Frontières", abbr: "CSF", color: "#8B5CF6" },
    { name: "Fontana Foundation", abbr: "FF", color: "#0891B2" }
  ];

  return (
    <section className="py-20 bg-white" data-testid="partners">
      <div className="container-narrow">
        <p className="text-center text-[#78716C] text-sm uppercase tracking-wider mb-10">Our Partners & Supporters</p>
        <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10">
          {partners.map((partner, i) => (
            <div 
              key={i} 
              className="group flex items-center gap-3 px-6 py-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all duration-300 cursor-default" 
              data-testid={`partner-${i}`}
            >
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                style={{ backgroundColor: partner.color }}
              >
                {partner.abbr}
              </div>
              <span className="text-sm font-medium text-gray-700 hidden md:block">{partner.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ HOME PAGE ============
const HomePage = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.post(`${API}/seed`).catch(() => {});
    axios.get(`${API}/stats`).then(res => setStats(res.data)).catch(() => {});
  }, []);

  const batches = [
    { id: 1, name: "First Batch", period: "Dec 2023 - Jun 2025", status: "Completed", students: 15, statusType: "completed" },
    { id: 2, name: "Second Batch", period: "Jul 2025 - Jun 2027", status: "Currently Running", students: 18, statusType: "active" },
    { id: 3, name: "Third Batch", period: "Starting July 2026", status: "Now Enrolling", students: 15, statusType: "upcoming" }
  ];

  const curriculum = [
    { title: "Professional Knowledge", type: "professional", skills: ["Cooking Methods", "Baking & Pastry", "Barista Skills", "Bulk Cooking", "Food Preservation", "Processing"] },
    { title: "Practical Training", type: "practical", skills: ["Central Kitchen Ops", "Satellite Kitchen", "Menu Planning", "Quality Control", "Hygiene Standards", "Equipment"] },
    { title: "General Knowledge", type: "general", skills: ["Computer Skills", "English Language", "Economics", "Food Systems", "Soft Skills", "Communication"] },
    { title: "Project Works", type: "project", skills: ["Business Planning", "Innovation", "Team Work", "Problem Solving", "Presentation", "Community Projects"] }
  ];

  const outcomes = [
    { icon: GraduationCap, title: "Certified Skills", desc: "CTEVT-recognized certification" },
    { icon: Briefcase, title: "Job Placement", desc: "Direct employment opportunities" },
    { icon: Building2, title: "Franchise Support", desc: "Launch your satellite kitchen" },
    { icon: Users, title: "Alumni Network", desc: "Join our growing community" }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section - Culinary Training in Nepal */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-orange-50 via-white to-green-50 overflow-hidden" data-testid="hero" aria-label="Hero section for Nepal's best culinary training institute">
        <div className="container-wide relative z-10 pt-24 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="badge badge-orange mb-8 animate-fade-up">
                <Award size={14} />
                <span>CTEVT Affiliated • Dual-VET Program Nepal</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif text-[#0D0D0D] mb-6 animate-fade-up delay-100">
                Best Vocational Education and Training Academy in Nepal:<br />
                <span className="text-[#F47920]">Mass Catering</span><br />
                Professionals Program
              </h1>
              
              <p className="text-lg text-[#78716C] mb-10 max-w-xl animate-fade-up delay-200">
                Sarvaguna Academy provides CTEVT-certified dual-vocational culinary education and chef training in Pokhara, Nepal. Join our 24-month cooking course and transform your passion into a professional food service career.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-up delay-300">
                <Link to="/apply" className="btn btn-primary" data-testid="hero-apply" aria-label="Apply for culinary training program">
                  Join Third Batch <ArrowRight size={18} />
                </Link>
                <Link to="/program" className="btn btn-outline" data-testid="hero-learn" aria-label="Explore culinary program details">
                  Explore Program
                </Link>
              </div>
            </div>
            
            <div className="hidden lg:block animate-fade-up delay-400" aria-hidden="true">
              <HeroIllustration />
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-10 text-6xl opacity-20 animate-float" aria-hidden="true">🥕</div>
        <div className="absolute bottom-20 left-10 text-5xl opacity-20 animate-float delay-200" aria-hidden="true">🥬</div>
        <div className="absolute top-1/3 right-1/4 text-4xl opacity-15 animate-float delay-300" aria-hidden="true">🍅</div>
      </section>

      {/* Batches Section - Vocational Training Cohorts */}
      <section className="section-padding bg-white" data-testid="batches-section" aria-label="Training batch information">
        <div className="container-narrow">
          <div className="text-center mb-16">
            <div className="divider mx-auto mb-6"></div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif mb-4">Our Academy Journey</h2>
            <p className="text-[#78716C] max-w-xl mx-auto">Building a generation of skilled mass catering professionals in Nepal, one batch at a time through quality vocational education and training.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {batches.map((batch) => (
              <article key={batch.id} className={`batch-card ${batch.statusType}`} data-testid={`batch-${batch.id}`}>
                <div className={`badge mb-4 ${batch.statusType === 'completed' ? 'badge-dark' : batch.statusType === 'active' ? 'badge-green' : 'badge-orange'}`}>
                  {batch.statusType === 'completed' && <CheckCircle2 size={14} />}
                  {batch.statusType === 'active' && <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>}
                  {batch.statusType === 'upcoming' && <Calendar size={14} />}
                  {batch.status}
                </div>
                <h3 className="text-xl font-serif mb-2">{batch.name}</h3>
                <p className="text-[#78716C] text-sm mb-4">{batch.period}</p>
                <div className="flex items-center gap-2 text-sm">
                  <Users size={16} className="text-[#F47920]" />
                  <span className="font-medium">{batch.students} {batch.statusType === 'upcoming' ? 'spots available' : 'apprentices'}</span>
                </div>
                {batch.statusType === 'upcoming' && (
                  <Link to="/apply" className="btn btn-primary w-full mt-6" data-testid="batch-apply" aria-label="Apply for Third Batch cooking course">
                    Apply Now <ArrowRight size={16} />
                  </Link>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Dual-VET Section - Vocational Education Model */}
      <section className="section-padding bg-[#FAFAF9]" data-testid="dual-vet" aria-label="Dual vocational education and training model">
        <div className="container-narrow">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="divider mb-6"></div>
              <h2 className="text-3xl md:text-4xl font-serif mb-6">
                Dual-VET in Nepal:<br />
                <span className="text-[#F47920]">Learn Cooking by Doing</span>
              </h2>
              <p className="text-[#78716C] mb-8 leading-relaxed">
                Our Dual Vocational Education and Training model combines theoretical classroom learning with hands-on practical chef training in real commercial kitchen environments. This Swiss-inspired dual-VET approach makes Sarvaguna Academy the best cooking school in Pokhara for aspiring food service professionals.
              </p>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="p-6 bg-white rounded-2xl border border-[#E7E5E4]">
                  <div className="stat-number text-[#F47920]">4</div>
                  <p className="text-sm text-[#78716C] mt-2">Days practical kitchen training</p>
                </div>
                <div className="p-6 bg-white rounded-2xl border border-[#E7E5E4]">
                  <div className="stat-number text-[#4CAF50]">2</div>
                  <p className="text-sm text-[#78716C] mt-2">Days classroom instruction</p>
                </div>
              </div>

              <Link to="/program" className="btn btn-ghost" data-testid="learn-more" aria-label="Learn more about our culinary program">
                Learn more about the program <ArrowUpRight size={18} />
              </Link>
            </div>

            <div className="hidden lg:block" aria-hidden="true">
              <DualVETIllustration />
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum - Culinary Skills Training */}
      <section className="section-padding bg-white" data-testid="curriculum" aria-label="Culinary curriculum and skills training">
        <div className="container-narrow">
          <div className="text-center mb-16">
            <div className="divider mx-auto mb-6"></div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif mb-4">Skills You'll Master</h2>
            <p className="text-[#78716C] max-w-xl mx-auto">A comprehensive mass catering professionals curriculum designed to make you industry-ready for food service careers in Nepal and beyond.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {curriculum.map((cat, i) => (
              <article key={i} className="card card-bordered p-8" data-testid={`curriculum-${i}`}>
                <div className="w-16 h-16 mb-6" aria-hidden="true">
                  <CurriculumIllustration type={cat.type} />
                </div>
                <h3 className="text-xl font-serif mb-4">{cat.title}</h3>
                <div className="flex flex-wrap gap-2">
                  {cat.skills.map((skill, j) => (
                    <span key={j} className="skill-tag">{skill}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes - Career Opportunities */}
      <section className="section-padding bg-[#0D0D0D] text-white" data-testid="outcomes" aria-label="Career outcomes and job placement">
        <div className="container-narrow">
          <div className="text-center mb-16">
            <div className="divider mx-auto mb-6"></div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif mb-4">Your Chef Career Starts Here</h2>
            <p className="text-white/60 max-w-xl mx-auto">Multiple career pathways after completing the CTEVT-certified program in Nepal.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {outcomes.map((item, i) => (
              <article key={i} className="text-center p-8 rounded-2xl border border-white/10 hover:border-[#F47920]/50 transition-colors" data-testid={`outcome-${i}`}>
                <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-6" aria-hidden="true">
                  <item.icon className="text-[#F47920]" size={28} />
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.desc}</p>
              </article>
            ))}
          </div>

          {/* Franchise CTA */}
          <div className="mt-16 p-8 md:p-12 rounded-3xl bg-gradient-to-r from-[#F47920]/20 to-[#4CAF50]/20 border border-white/10">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl md:text-3xl font-serif mb-4">Launch Your Own Kitchen Business</h3>
                <p className="text-white/70">Graduates can partner with us to open satellite kitchens, becoming entrepreneurs in Nepal's sustainable food business industry.</p>
                <Link to="/contact" className="btn btn-primary mt-6" data-testid="franchise-cta" aria-label="Learn about kitchen franchise opportunities">
                  Learn About Franchising <ArrowRight size={18} />
                </Link>
              </div>
              <div className="hidden lg:block" aria-hidden="true">
                <FranchiseIllustration />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#F47920]" data-testid="cta" aria-label="Call to action for culinary training enrollment">
        <div className="container-narrow text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-6">
            Ready to Start Your Chef Journey in Nepal?
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-xl mx-auto">
            Third batch enrollments are now open at Nepal's best Vocational Education and Training Academy. Only 15 spots available for aspiring mass catering professionals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/apply" className="btn bg-white text-[#F47920] hover:bg-gray-100 px-8 py-4 rounded-full font-semibold" data-testid="cta-apply" aria-label="Apply for Third Batch culinary program">
              Apply for Third Batch <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="btn border-2 border-white text-white hover:bg-white hover:text-[#F47920] px-8 py-4 rounded-full font-semibold" data-testid="cta-contact" aria-label="Contact us for culinary training inquiries">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Partners */}
      <PartnerLogos />
    </div>
  );
};

// ============ PROGRAM PAGE ============
const ProgramPage = () => {
  const schedule = [
    { day: "Sun", type: "Practical", color: "orange" },
    { day: "Mon", type: "Practical", color: "orange" },
    { day: "Tue", type: "Practical", color: "orange" },
    { day: "Wed", type: "Practical", color: "orange" },
    { day: "Thu", type: "School", color: "green" },
    { day: "Fri", type: "School", color: "green" },
    { day: "Sat", type: "Off", color: "gray" }
  ];

  const skills = [
    { icon: Utensils, name: "Cooking Methods", desc: "Master various cooking techniques" },
    { icon: Coffee, name: "Barista Skills", desc: "Coffee preparation & service" },
    { icon: Scale, name: "Bulk Cooking", desc: "High-volume production" },
    { icon: ChefHat, name: "Baking & Pastry", desc: "Breads, pastries, desserts" },
    { icon: Heart, name: "Food Preservation", desc: "Safe storage & processing" },
    { icon: Monitor, name: "Computer Skills", desc: "Digital kitchen tools" },
    { icon: Languages, name: "English Language", desc: "Professional communication" },
    { icon: BookOpen, name: "Food Systems", desc: "Farm-to-fork knowledge" }
  ];

  return (
    <div className="pt-20" data-testid="program-page">
      <section className="section-padding bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="container-narrow">
          <div className="badge badge-orange mb-6">24 Months • CTEVT Certified</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif mb-6">
            Mass Catering<br /><span className="text-[#F47920]">Professionals Program</span>
          </h1>
          <p className="text-xl text-[#78716C] max-w-2xl mb-10">
            A comprehensive dual-vocational education and training program combining classroom learning with hands-on experience.
          </p>
          <Link to="/apply" className="btn btn-primary" data-testid="program-apply">
            Apply Now <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Schedule */}
      <section className="section-padding bg-white">
        <div className="container-narrow">
          <div className="text-center mb-16">
            <div className="divider mx-auto mb-6"></div>
            <h2 className="text-3xl md:text-4xl font-serif mb-4">Weekly Schedule</h2>
            <p className="text-[#78716C]">4 days hands-on, 2 days classroom</p>
          </div>

          <div className="grid grid-cols-7 gap-2 md:gap-4">
            {schedule.map((day, i) => (
              <div key={i} className={`p-3 md:p-6 rounded-xl text-center ${
                day.color === 'orange' ? 'bg-orange-50 border-2 border-orange-200' :
                day.color === 'green' ? 'bg-green-50 border-2 border-green-200' :
                'bg-gray-50 border-2 border-gray-200'
              }`}>
                <div className="font-semibold text-sm mb-1">{day.day}</div>
                <div className={`text-xs font-medium ${
                  day.color === 'orange' ? 'text-[#F47920]' :
                  day.color === 'green' ? 'text-[#4CAF50]' : 'text-gray-500'
                }`}>{day.type}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills */}
      <section className="section-padding bg-[#FAFAF9]">
        <div className="container-narrow">
          <div className="text-center mb-16">
            <div className="divider mx-auto mb-6"></div>
            <h2 className="text-3xl md:text-4xl font-serif mb-4">Skills You'll Master</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {skills.map((skill, i) => (
              <div key={i} className="card card-bordered p-6 hover:border-[#F47920]">
                <skill.icon className="text-[#F47920] mb-4" size={28} />
                <h3 className="font-semibold mb-2">{skill.name}</h3>
                <p className="text-sm text-[#78716C]">{skill.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="section-padding bg-white">
        <div className="container-narrow">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="divider mb-6"></div>
              <h2 className="text-3xl md:text-4xl font-serif mb-6">Who Can Apply?</h2>
              <div className="space-y-4">
                {["Age 18 years or above", "Minimum SLC/SEE level certificate", "Passion for food service industry", "Commitment to 24-month program", "Priority to underserved communities"].map((req, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="text-[#4CAF50]" size={20} />
                    <span>{req}</span>
                  </div>
                ))}
              </div>
              <Link to="/apply" className="btn btn-primary mt-8">
                Start Your Application <ArrowRight size={18} />
              </Link>
            </div>
            <div className="hidden lg:block">
              <DualVETIllustration />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============ ABOUT PAGE ============
const AboutPage = () => {
  const team = [
    { name: "Hari Baral", role: "Academic Director / Chef Trainer", type: "leadership" },
    { name: "Anju Poudel", role: "Academy Coordinator", type: "leadership" },
    { name: "Trainer 1", role: "Culinary Instructor", type: "trainer", placeholder: true },
    { name: "Trainer 2", role: "Baking Instructor", type: "trainer", placeholder: true },
    { name: "Trainer 3", role: "General Studies", type: "trainer", placeholder: true },
  ];

  return (
    <div className="pt-20" data-testid="about-page">
      <section className="section-padding bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="container-narrow">
          <div className="divider mb-6"></div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif mb-6">
            About <span className="text-[#F47920]">Sarvaguna Academy</span>
          </h1>
          <p className="text-xl text-[#78716C] max-w-2xl">
            An initiative of Sarvaguna Foods & Kitchen, empowering young people through dual-vocational education in mass catering.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-white">
        <div className="container-narrow">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="p-10 bg-[#F47920] rounded-3xl text-white">
              <h3 className="text-2xl font-serif mb-4">Our Mission</h3>
              <p className="text-white/90 leading-relaxed">
                To empower young people from underserved communities with world-class vocational skills in mass catering, creating a pipeline of professionals who will transform Nepal's food service industry.
              </p>
            </div>
            <div className="p-10 bg-[#4CAF50] rounded-3xl text-white">
              <h3 className="text-2xl font-serif mb-4">Our Vision</h3>
              <p className="text-white/90 leading-relaxed">
                To become Nepal's leading dual-VET institution for food service professionals, setting standards for excellence through sustainable, farm-to-fork education.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Company */}
      <section className="section-padding bg-[#FAFAF9]" data-testid="our-company">
        <div className="container-narrow">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="divider mb-6"></div>
              <h2 className="text-3xl md:text-4xl font-serif mb-6">Our Parent Company</h2>
              <p className="text-[#78716C] mb-6 leading-relaxed">
                <strong className="text-[#0D0D0D]">Sarvaguna Foods & Kitchen</strong> is a social enterprise from Pokhara, Nepal delivering nutritious meals to schools, hospitals, and events. We connect 500+ farmers to consumers, serve 9,000+ meals daily, and empower communities through sustainable food systems.
              </p>
              <p className="text-[#78716C] mb-8 leading-relaxed">
                Sarvaguna Academy was established as an initiative of Sarvaguna Foods & Kitchen to address the growing need for skilled food service professionals in Nepal. Our apprentices train in the same central and satellite kitchens that serve thousands of meals daily.
              </p>
              <a 
                href="https://sarvagunakitchen.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-outline inline-flex items-center gap-2"
                data-testid="company-link"
              >
                Visit Sarvaguna Foods & Kitchen <ExternalLink size={16} />
              </a>
            </div>
            <div className="bg-white p-8 rounded-3xl border border-[#E7E5E4]">
              <img 
                src="https://customer-assets.emergentagent.com/job_courses-staging/artifacts/iwd9p0t9_Sarvaguna%20Logo-%20CMYK.jpg" 
                alt="Sarvaguna Foods & Kitchen" 
                className="h-24 mx-auto mb-6 object-contain"
              />
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-4 bg-orange-50 rounded-xl">
                  <div className="text-2xl font-serif text-[#F47920]">9,000+</div>
                  <p className="text-sm text-[#78716C]">Meals Daily</p>
                </div>
                <div className="p-4 bg-green-50 rounded-xl">
                  <div className="text-2xl font-serif text-[#4CAF50]">500+</div>
                  <p className="text-sm text-[#78716C]">Farmers Connected</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-xl">
                  <div className="text-2xl font-serif text-[#F47920]">100+</div>
                  <p className="text-sm text-[#78716C]">Jobs Created</p>
                </div>
                <div className="p-4 bg-green-50 rounded-xl">
                  <div className="text-2xl font-serif text-[#4CAF50]">60%</div>
                  <p className="text-sm text-[#78716C]">Women Employees</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding bg-white" data-testid="team-section">
        <div className="container-narrow">
          <div className="text-center mb-16">
            <div className="divider mx-auto mb-6"></div>
            <h2 className="text-3xl md:text-4xl font-serif mb-4">Our Team</h2>
            <p className="text-[#78716C]">Meet the people behind Sarvaguna Academy</p>
          </div>

          {/* Leadership */}
          <div className="mb-12">
            <h3 className="text-lg font-semibold text-[#78716C] mb-6 uppercase tracking-wider">Leadership</h3>
            <div className="grid sm:grid-cols-2 gap-6">
              {team.filter(t => t.type === 'leadership').map((member, i) => (
                <div key={i} className="flex items-center gap-6 p-6 bg-[#FAFAF9] rounded-2xl" data-testid={`team-${i}`}>
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-200 to-green-200 rounded-full flex items-center justify-center text-2xl font-serif text-[#F47920]">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">{member.name}</h4>
                    <p className="text-[#78716C]">{member.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Trainers */}
          <div>
            <h3 className="text-lg font-semibold text-[#78716C] mb-6 uppercase tracking-wider">Trainers & Instructors</h3>
            <div className="grid sm:grid-cols-3 gap-6">
              {team.filter(t => t.type === 'trainer').map((member, i) => (
                <div key={i} className={`p-6 rounded-2xl text-center ${member.placeholder ? 'bg-gray-100 border-2 border-dashed border-gray-300' : 'bg-[#FAFAF9]'}`}>
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${member.placeholder ? 'bg-gray-200' : 'bg-gradient-to-br from-orange-200 to-green-200'}`}>
                    {member.placeholder ? (
                      <Users className="text-gray-400" size={24} />
                    ) : (
                      <span className="text-xl font-serif text-[#F47920]">{member.name.split(' ').map(n => n[0]).join('')}</span>
                    )}
                  </div>
                  <h4 className={`font-semibold ${member.placeholder ? 'text-gray-400' : ''}`}>{member.placeholder ? 'To be updated' : member.name}</h4>
                  <p className="text-sm text-[#78716C]">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-[#FAFAF9]">
        <div className="container-narrow">
          <div className="text-center mb-16">
            <div className="divider mx-auto mb-6"></div>
            <h2 className="text-3xl md:text-4xl font-serif">Our Values</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: "Excellence", desc: "Highest standards in dual-vocational education and training" },
              { title: "Sustainability", desc: "Farm-to-fork philosophy" },
              { title: "Community", desc: "Priority to underserved youth" },
              { title: "Innovation", desc: "Modern approaches to skills" }
            ].map((val, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-serif text-[#F47920]">{val.title[0]}</span>
                </div>
                <h3 className="font-semibold mb-2">{val.title}</h3>
                <p className="text-sm text-[#78716C]">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// ============ GALLERY PAGE ============
const GalleryPage = () => {
  const [images, setImages] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/gallery`).then(res => setImages(res.data)).catch(() => {});
  }, []);

  const categories = ["all", ...new Set(images.map(img => img.category))];
  const filtered = filter === "all" ? images : images.filter(img => img.category === filter);

  return (
    <div className="pt-20" data-testid="gallery-page">
      <section className="section-padding bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="container-narrow">
          <div className="divider mb-6"></div>
          <h1 className="text-4xl md:text-5xl font-serif mb-6">Gallery</h1>
          <p className="text-xl text-[#78716C]">Moments from our training sessions and events.</p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide">
          <div className="flex flex-wrap gap-3 mb-12 justify-center">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === cat ? "bg-[#F47920] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((img) => (
              <div key={img.id} className="group relative rounded-2xl overflow-hidden aspect-[4/3]">
                <img src={img.image_url} alt={img.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                  <div className="text-white">
                    <h4 className="font-semibold">{img.title}</h4>
                    <p className="text-sm text-white/70">{img.category}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// ============ CONTACT PAGE ============
const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent successfully!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      <section className="section-padding bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="container-narrow">
          <div className="divider mb-6"></div>
          <h1 className="text-4xl md:text-5xl font-serif mb-6">Get in Touch</h1>
          <p className="text-xl text-[#78716C]">Questions about our program? We'd love to hear from you.</p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-narrow">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="space-y-6">
              {[
                { icon: Phone, title: "Phone", content: ["+977 9802852978", "+977 9846771411"] },
                { icon: Mail, title: "Email", content: ["info@sarvagunaacademy.com"] },
                { icon: MapPin, title: "Location", content: ["Pokhara, Nepal"] }
              ].map((item, i) => (
                <div key={i} className="p-6 bg-[#FAFAF9] rounded-2xl">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="text-[#F47920]" size={22} />
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  {item.content.map((c, j) => <p key={j} className="text-[#78716C] text-sm">{c}</p>)}
                </div>
              ))}
            </div>

            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="p-8 md:p-10 bg-[#FAFAF9] rounded-3xl" data-testid="contact-form">
                <h3 className="text-2xl font-serif mb-8">Send a Message</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <input type="text" placeholder="Your Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="input" required data-testid="contact-name" />
                  <input type="email" placeholder="Email Address" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="input" required data-testid="contact-email" />
                </div>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <input type="tel" placeholder="Phone (Optional)" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="input" />
                  <input type="text" placeholder="Subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} className="input" required data-testid="contact-subject" />
                </div>
                <textarea rows={5} placeholder="Your Message" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="input resize-none mb-6" required data-testid="contact-message"></textarea>
                <button type="submit" disabled={loading} className="btn btn-primary w-full" data-testid="contact-submit">
                  {loading ? <span className="spinner"></span> : <>Send Message <Send size={18} /></>}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============ APPLICATION PAGE ============
const ApplicationPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", address: "", date_of_birth: "",
    education_level: "", program_interest: "Mass Catering Professionals",
    previous_experience: "", motivation: "", entrepreneurial_goals: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, form);
      setSuccess(true);
      toast.success("Application submitted!");
    } catch (err) {
      toast.error("Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-green-50" data-testid="application-success">
        <div className="text-center max-w-md p-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="text-[#4CAF50]" size={48} />
          </div>
          <h2 className="text-3xl font-serif mb-4">Application Submitted!</h2>
          <p className="text-[#78716C] mb-8">Thank you for applying. We'll contact you soon.</p>
          <Link to="/" className="btn btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="application-page">
      <section className="py-16 bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="container-narrow">
          <div className="badge badge-orange mb-6">Third Batch • July 2026 • 15 Spots</div>
          <h1 className="text-4xl md:text-5xl font-serif mb-4">Apply Now</h1>
          <p className="text-[#78716C]">Join Nepal's premier dual-VET program in mass catering.</p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-narrow max-w-2xl">
          <div className="flex items-center justify-center gap-4 mb-12">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${step >= s ? "bg-[#F47920] text-white" : "bg-gray-200 text-gray-500"}`}>{s}</div>
                {s < 3 && <div className={`w-12 h-1 rounded ${step > s ? "bg-[#F47920]" : "bg-gray-200"}`}></div>}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="p-8 md:p-10 bg-[#FAFAF9] rounded-3xl" data-testid="application-form">
            {step === 1 && (
              <div className="space-y-6">
                <h3 className="text-2xl font-serif mb-6">Personal Information</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <input type="text" placeholder="Full Name *" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} className="input" required data-testid="app-name" />
                  <input type="date" value={form.date_of_birth} onChange={(e) => setForm({...form, date_of_birth: e.target.value})} className="input" required data-testid="app-dob" />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <input type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="input" required data-testid="app-email" />
                  <input type="tel" placeholder="Phone *" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="input" required data-testid="app-phone" />
                </div>
                <input type="text" placeholder="Address *" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="input" required data-testid="app-address" />
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <h3 className="text-2xl font-serif mb-6">Education & Background</h3>
                <select value={form.education_level} onChange={(e) => setForm({...form, education_level: e.target.value})} className="input" required>
                  <option value="">Select Education Level *</option>
                  <option value="SLC/SEE">SLC/SEE</option>
                  <option value="+2/Intermediate">+2/Intermediate</option>
                  <option value="Bachelor's">Bachelor's Degree</option>
                  <option value="Other">Other</option>
                </select>
                <div className="p-4 bg-white rounded-xl border border-[#E7E5E4]">
                  <p className="text-sm text-[#78716C] mb-2">Program</p>
                  <p className="font-semibold">Mass Catering Professionals (24 Months)</p>
                </div>
                <textarea rows={4} placeholder="Previous Experience (Optional)" value={form.previous_experience} onChange={(e) => setForm({...form, previous_experience: e.target.value})} className="input resize-none"></textarea>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <h3 className="text-2xl font-serif mb-6">Your Motivation</h3>
                <textarea rows={5} placeholder="Why do you want to join Sarvaguna Academy? *" value={form.motivation} onChange={(e) => setForm({...form, motivation: e.target.value})} className="input resize-none" required data-testid="app-motivation"></textarea>
                <textarea rows={4} placeholder="Entrepreneurial Goals (Optional)" value={form.entrepreneurial_goals} onChange={(e) => setForm({...form, entrepreneurial_goals: e.target.value})} className="input resize-none"></textarea>
              </div>
            )}

            <div className="flex justify-between mt-8 pt-6 border-t border-[#E7E5E4]">
              {step > 1 && <button type="button" onClick={() => setStep(step - 1)} className="btn btn-outline">Previous</button>}
              {step < 3 ? (
                <button type="button" onClick={() => setStep(step + 1)} className="btn btn-primary ml-auto">Continue</button>
              ) : (
                <button type="submit" disabled={loading} className="btn btn-primary ml-auto">
                  {loading ? <span className="spinner"></span> : <>Submit Application <Send size={18} /></>}
                </button>
              )}
            </div>
          </form>

          <div className="mt-8 p-6 bg-orange-50 rounded-2xl">
            <h4 className="font-semibold mb-4">Requirements</h4>
            <div className="grid sm:grid-cols-2 gap-3 text-sm text-[#78716C]">
              {["Age 18+", "Min. SLC/SEE", "2-year commitment", "Passion for food"].map((r, i) => (
                <div key={i} className="flex items-center gap-2"><CheckCircle2 className="text-[#4CAF50]" size={16} />{r}</div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============ MAIN APP ============
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Header />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/program" element={<ProgramPage />} />
            <Route path="/programs" element={<ProgramPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/apply" element={<ApplicationPage />} />
          </Routes>
        </main>
        <Footer />
        <WhatsAppButton />
        <Toaster position="bottom-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
